import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, FileText, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

export default function AdminLogin() {
  const [email, setEmail] = useState(localStorage.getItem("si-admin-email") || "admin@simpleinvoice.com");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password, remember_me: remember });
      localStorage.setItem("si-admin-token", r.data.token);
      if (remember) localStorage.setItem("si-admin-email", email);
      else localStorage.removeItem("si-admin-email");
      toast.success(`Welcome back, ${r.data.name || "Admin"}`);
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 ambient-glow px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <span className="w-11 h-11 rounded-xl bg-blue-600 text-white grid place-items-center shadow-md"><FileText size={20} /></span>
          <div>
            <div className="font-heading font-bold text-lg leading-tight">Simple Invoice</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin panel</div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl bg-card border border-border p-8 shadow-xl">
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to manage your website.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Email or Username</Label>
              <Input data-testid="admin-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Password</Label>
              <div className="relative">
                <Input data-testid="admin-password" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="admin-remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} />
                <span className="text-sm">Remember me</span>
              </label>
              <Link to="/admin/forgot-password" data-testid="forgot-link" className="text-sm text-blue-600 hover:underline">Forgot password?</Link>
            </div>

            <Button data-testid="admin-submit" type="submit" disabled={loading} className="w-full rounded-xl h-11 bg-blue-600 hover:bg-blue-700 text-white">
              <Lock size={14} className="mr-2" /> {loading ? "Signing in..." : "Sign in"}
            </Button>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            Protected admin area. All logins are logged.
          </div>
        </form>
      </div>
    </div>
  );
}
